//src/components/features/coin-claim/components/CoinInWalletAnim.tsx
'use client';

import * as React from 'react';
import Image from 'next/image';
import styles from './CoinInWalletAnim.module.scss';
import Coin from '@/components/ui/coin/Coin';
import Avatar from '@/components/ui/avatar/Avatar';

type Props = {
  color: 'green' | 'red' | 'yellow';
  avatarSrc: string;
  scale?: number;
  /** 進場動畫的延遲（毫秒），預設 0 */
  delayMs?: number;
  /** 音效網址（給空就不播） */
  soundUrl?: string;
  /** 是否播放音效（預設 true） */
  playSound?: boolean;
  /** 音量 0~1（預設 1） */
  soundVolume?: number;
};

const WALLET = 'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/wallet.svg';
const WALLET_COVER = 'https://d1q14jmvwk39e0.cloudfront.net/public/assets/images/wallet2.svg';
const Sound_Url = 'https://d1q14jmvwk39e0.cloudfront.net/public/assets/mp3/capsule.mp3';

export default function CoinInWalletAnim({
  color,
  avatarSrc,
  scale = 0.5,
  delayMs = 0,
  soundUrl = Sound_Url,
  playSound = true,
  soundVolume = 1,
}: Props) {
  const [enter, setEnter] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  React.useEffect(() => {
    // 掛載下一個 tick 觸發進場動畫
    const t = setTimeout(() => setEnter(true), Math.max(0, delayMs));
    return () => clearTimeout(t);
  }, [delayMs]);

  React.useEffect(() => {
    // 進場時播放音效（需有使用者互動才能保證自動播放，多數情境下開 modal 來自點擊可播放）
    if (!enter || !playSound || !soundUrl) return;

    try {
      // 若已建立 audio，先停止/釋放
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
      }
      const audio = new Audio(soundUrl);
      audio.volume = Math.min(1, Math.max(0, soundVolume ?? 1));
      audioRef.current = audio;
      // 不循環、只播一次
      audio.play().catch(() => {
        // 某些瀏覽器策略會阻止自動播放；此處靜默失敗避免報錯
      });
    } catch {
      // 忽略音效錯誤
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.load();
        audioRef.current = null;
      }
    };
  }, [enter, playSound, soundUrl, soundVolume]);

  return (
    <div className={styles.root}>
      {/* 幣：淡入 + 向下滑動 */}
      <div className={enter ? `${styles.coinBox} ${styles.enter}` : styles.coinBox}>
        {color === 'yellow' ? (
          <Avatar src={avatarSrc} size={120} />
        ) : (
          <Coin scale={scale} color={color}>
            <Avatar src={avatarSrc} />
          </Coin>
        )}
      </div>

      {/* 錢包（底圖 + 會翻開的蓋子） */}
      <div className={styles.walletBox}>
        <Image src={WALLET} alt="wallet" width={156} height={96} className={styles.wallet} />
        <div className={enter ? `${styles.walletCover} ${styles.enter}` : styles.walletCover}>
          <Image src={WALLET_COVER} alt="wallet cover" width={156} height={96} />
        </div>
      </div>
    </div>
  );
}
